import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAnnouncement,
  deleteAnnouncement,
  listAnnouncements,
  retranslateAnnouncement,
  updateAnnouncement,
} from 'services/announcements';
import type { AnnouncementInput } from 'types/announcements';

export const ANNOUNCEMENTS_QUERY_KEY = ['announcements'] as const;

export function useAnnouncementsQuery() {
  return useQuery({
    queryKey: ANNOUNCEMENTS_QUERY_KEY,
    queryFn: listAnnouncements,
    staleTime: 30_000,
  });
}

function useInvalidateAnnouncements() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ANNOUNCEMENTS_QUERY_KEY });
}

export function useCreateAnnouncementMutation() {
  const invalidate = useInvalidateAnnouncements();
  return useMutation({ mutationFn: createAnnouncement, onSuccess: () => invalidate() });
}

export function useUpdateAnnouncementMutation() {
  const invalidate = useInvalidateAnnouncements();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      patch: Partial<AnnouncementInput>;
      retranslate?: boolean;
    }) => updateAnnouncement(vars.id, vars.patch, { retranslate: vars.retranslate }),
    onSuccess: () => invalidate(),
  });
}

export function useRetranslateAnnouncementMutation() {
  const invalidate = useInvalidateAnnouncements();
  return useMutation({ mutationFn: retranslateAnnouncement, onSuccess: () => invalidate() });
}

export function useDeleteAnnouncementMutation() {
  const invalidate = useInvalidateAnnouncements();
  return useMutation({ mutationFn: deleteAnnouncement, onSuccess: () => invalidate() });
}
